import React, { useEffect, useState } from 'react';
import { Phone, PhoneForwarded, PhoneIncoming, User, Clock, Globe, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
 
interface QueuedCall {
  id: string;
  call_sid: string;
  from_number: string;
  caller_name: string | null;
  status: string;
  assigned_agent_id: string | null;
  created_at: string;
  metadata: { language?: string; call_reason?: string; [key: string]: unknown };
}
 
const LANGUAGE_LABELS: Record<string, string> = { fr: 'Français', en: 'English', es: 'Español' };
const REASON_LABELS: Record<string, string> = {
  new_submission: 'Nouvelle soumission', follow_up: 'Suivi de dossier',
  speak_advisor: 'Parler à un conseiller', other: 'Autre demande',
};
 
export function IncomingCallQueue({ className }: { className?: string }) {
  const { user } = useAuth();
  const [queuedCalls, setQueuedCalls] = useState<QueuedCall[]>([]);
  const [elapsed, setElapsed] = useState<Record<string, number>>({});
 
  useEffect(() => {
    if (!user?.id) return;
 
    const q = query(
      collection(db, 'incomingCalls'),
      where('status', 'in', ['ringing', 'queued'])
    );
 
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const calls: QueuedCall[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          call_sid: data.callSid || data.call_sid,
          from_number: data.fromNumber || data.from_number,
          caller_name: data.callerName || data.caller_name || null,
          status: data.status,
          assigned_agent_id: data.assignedAgentId || data.assigned_agent_id || null,
          created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          metadata: data.metadata || {},
        };
      });
 
      const sorted = calls.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
 
      // Son si nouvel appel
      if (sorted.length > queuedCalls.length) {
        try {
          const ctx = new AudioContext();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = 520; gain.gain.value = 0.2;
          osc.start(); setTimeout(() => osc.stop(), 150);
        } catch {}
      }
 
      setQueuedCalls(sorted);
    });
 
    return () => unsubscribe();
  }, [user?.id]);
 
  // Timer d'attente
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const e: Record<string, number> = {};
      queuedCalls.forEach(c => { e[c.id] = Math.floor((now - new Date(c.created_at).getTime()) / 1000); });
      setElapsed(e);
    }, 1000);
    return () => clearInterval(interval);
  }, [queuedCalls]);
 
  const handleAccept = async (call: QueuedCall) => {
    const { error } = await updateDoc(doc(db, 'incomingCalls', call.id), {
      status: 'answered',
      answeredAt: serverTimestamp(),
      assignedAgentId: user?.id,
    }).then(() => ({ error: null })).catch(e => ({ error: e }));
 
    if (error) toast.error("Erreur lors de l'acceptation de l'appel");
    else toast.success(`Appel accepté — ${call.caller_name || call.from_number}`);
  };
 
  const handleTransfer = async (_call: QueuedCall) => {
    toast.info('Fonctionnalité de transfert à venir');
  };
 
  const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
 
  const fmtPhone = (p: string) => {
    const c = p.replace(/\D/g, '');
    if (c.length === 11 && c.startsWith('1')) return `+1 (${c.slice(1, 4)}) ${c.slice(4, 7)}-${c.slice(7)}`;
    return p;
  };
 
  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <PhoneIncoming className="h-5 w-5 text-primary" />
          File d'attente
          {queuedCalls.length > 0 && <Badge variant="destructive" className="animate-pulse">{queuedCalls.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {queuedCalls.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <PhoneIncoming className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucun appel en attente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queuedCalls.map((call, index) => {
              const lang = call.metadata?.language as string | undefined;
              const reason = call.metadata?.call_reason as string | undefined;
              const wait = elapsed[call.id] || 0;
              return (
                <div key={call.id} className={cn('p-3 rounded-lg border-2 transition-all',
                  index === 0 ? 'border-green-500 bg-green-50/60 dark:bg-green-950/20 animate-pulse' : 'border-border bg-muted/30')}>
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-1 min-w-[36px]">
                      <span className="text-xs text-muted-foreground">#{index + 1}</span>
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {call.caller_name?.[0] || <User className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{call.caller_name || 'Inconnu'}</span>
                        <span className="text-xs text-muted-foreground">{fmtPhone(call.from_number)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {lang && <Badge variant="outline" className="text-xs gap-1 h-5"><Globe className="h-3 w-3" />{LANGUAGE_LABELS[lang] || lang}</Badge>}
                        {reason && <Badge variant="secondary" className="text-xs gap-1 h-5"><MessageSquare className="h-3 w-3" />{REASON_LABELS[reason] || reason}</Badge>}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>Attente : {fmt(wait)}</span>
                        {queuedCalls.length > 1 && <span className="ml-2">• {index === 0 ? 'Prochain' : `${index} appel${index > 1 ? 's' : ''} avant`}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => handleTransfer(call)}>
                        <PhoneForwarded className="h-3.5 w-3.5" />Transférer
                      </Button>
                      <Button size="sm" className="gap-1 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => handleAccept(call)}>
                        <Phone className="h-3.5 w-3.5" />Accepter
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
 